import React, { useContext, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoChevronBack, IoEye, IoEyeOff, IoLockClosed, IoPhonePortrait } from "react-icons/io5";

import { forgotPassword, login } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import LanguageContext from "../context/LanguageContext";
import { assetPath } from "../utils/assets";

const PHONE_MIN_LENGTH = 8;
const PHONE_MAX_LENGTH = 15;

const Login = () => {
    const { t } = useTranslation();
    const { token, setAuthToken } = useAuth();
    const { getLanguageId } = useContext(LanguageContext);

    const [form, setForm] = useState({
        phoneNumber: "",
        password: "",
    });
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [resetSubmitting, setResetSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [authRedirectPath, setAuthRedirectPath] = useState("");

    useEffect(() => {
        if (errorMessage || successMessage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [errorMessage, successMessage]);

    if (authRedirectPath) {
        return <Navigate to={authRedirectPath} replace />;
    }

    if (token) {
        return <Navigate to="/" replace />;
    }

    const handleChange = (key) => (event) => {
        let nextValue = event.target.value;

        if (key === "phoneNumber") {
            nextValue = nextValue.replace(/\D/g, "");
        }

        setForm((prev) => ({
            ...prev,
            [key]: nextValue,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const phoneNumber = (form.phoneNumber || "").trim();
            const password = form.password || "";

            if (phoneNumber.length < PHONE_MIN_LENGTH || phoneNumber.length > PHONE_MAX_LENGTH) {
                setErrorMessage(t("invalidPhoneNumber"));
                return;
            }

            const response = await login({
                phoneNumber,
                password,
            });

            if (response.success && response.data) {
                const nextPath = Number(response?.web_password_set) === 0 ? "/change-password" : "/";
                setAuthToken(response.data, phoneNumber, response.language);
                setAuthRedirectPath(nextPath);
            } else {
                setErrorMessage(response.data || t("pleaseTryLater"));
            }
        } catch (error) {
            setErrorMessage(t("pleaseTryLater"));
        } finally {
            setSubmitting(false);
        }
    };

    const handleForgotPassword = async () => {
        setErrorMessage("");
        setSuccessMessage("");

        const phoneNumber = (form.phoneNumber || "").trim();

        if (phoneNumber.length < PHONE_MIN_LENGTH || phoneNumber.length > PHONE_MAX_LENGTH) {
            setErrorMessage(t("enterPhoneForPasswordReset"));
            return;
        }

        setResetSubmitting(true);

        try {
            const response = await forgotPassword({
                phoneNumber,
                languageId: getLanguageId?.() || Number(localStorage.getItem("language_id")) || undefined,
            });

            if (response.success) {
                setSuccessMessage(response.data || t("passwordResetEmailSent"));
            } else {
                setErrorMessage(response.data || t("passwordResetEmailNotAvailable"));
            }
        } catch (error) {
            setErrorMessage(t("pleaseTryLater"));
        } finally {
            setResetSubmitting(false);
        }
    };

    return (
        <section className="relative min-h-screen overflow-hidden bg-[#031523] text-white">
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(17,155,255,0.2)_0%,_transparent_34%),linear-gradient(180deg,_#07253A_0%,_#031523_42%,_#02111A_100%)]" />
                <div className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-[#00D9FF]/10 blur-3xl" />
            </div>

            <div className="relative mx-auto flex min-h-screen w-full max-w-7xl items-center justify-center px-3 py-2 sm:px-4 sm:py-8 lg:px-8">
                <div className="w-full max-w-[460px]">
                    <div className="mb-2 flex items-center justify-between sm:mb-5">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/70 transition-colors duration-300 hover:bg-white/10 hover:text-white sm:gap-2 sm:px-3 sm:py-2 sm:text-xs sm:tracking-[0.16em]"
                        >
                            <IoChevronBack className="text-sm" />
                            <span>{t("backToLobby")}</span>
                        </Link>

                        <img src={assetPath("/icons/logo.png")} alt="Indo368" className="w-[104px] sm:w-[128px]" />
                    </div>

                    <div className="rounded-[1.25rem] bg-[#082032]/92 p-3 shadow-[0_32px_90px_-54px_rgba(0,0,0,0.9)] ring-1 ring-[#0E4F77] backdrop-blur-xl sm:rounded-[2rem] sm:p-7">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-[#84C9E5]/70 sm:text-[11px] sm:tracking-[0.28em]">
                                {t("login")}
                            </p>
                            <h1 className="mt-1 text-xl font-black text-white sm:mt-3 sm:text-3xl">
                                {t("welcomeBack")}
                            </h1>
                        </div>

                        {errorMessage && (
                            <div className="mt-3 rounded-[1rem] bg-[#3A1318] px-3 py-2 text-xs text-white ring-1 ring-[#7E2D37] sm:mt-5 sm:rounded-[1.2rem] sm:px-4 sm:py-3 sm:text-sm">
                                {errorMessage}
                            </div>
                        )}

                        {successMessage && (
                            <div className="mt-3 rounded-[1rem] bg-[#10351F] px-3 py-2 text-xs text-white ring-1 ring-[#2D7E49] sm:mt-5 sm:rounded-[1.2rem] sm:px-4 sm:py-3 sm:text-sm">
                                {successMessage}
                            </div>
                        )}

                        <form className="mt-3 space-y-2 sm:mt-6 sm:space-y-4" onSubmit={handleSubmit}>
                            <label className="block" htmlFor="login-phone">
                                <span className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#84C9E5]/72 sm:mb-2 sm:gap-2 sm:text-[11px] sm:tracking-[0.22em]">
                                    <IoPhonePortrait className="text-sm" />
                                    {t("phoneNumber")}
                                </span>
                                <div className="flex items-center rounded-[1rem] bg-[#061A2A] px-3 ring-1 ring-[#0D3853] transition-colors duration-300 focus-within:ring-[#21C8FF] sm:rounded-[1.3rem] sm:px-4">
                                    <input
                                        id="login-phone"
                                        type="tel"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={PHONE_MAX_LENGTH}
                                        value={form.phoneNumber}
                                        onChange={handleChange("phoneNumber")}
                                        className="h-9 w-full bg-transparent text-sm font-medium text-white outline-none placeholder:text-white/30 sm:h-14 sm:text-base"
                                        placeholder="62812345678"
                                    />
                                </div>
                            </label>

                            <label className="block" htmlFor="login-password">
                                <span className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#84C9E5]/72 sm:mb-2 sm:gap-2 sm:text-[11px] sm:tracking-[0.22em]">
                                    <IoLockClosed className="text-sm" />
                                    {t("password")}
                                </span>
                                <div className="flex items-center rounded-[1rem] bg-[#061A2A] px-3 ring-1 ring-[#0D3853] transition-colors duration-300 focus-within:ring-[#21C8FF] sm:rounded-[1.3rem] sm:px-4">
                                    <input
                                        id="login-password"
                                        type={showPassword ? "text" : "password"}
                                        value={form.password}
                                        onChange={handleChange("password")}
                                        className="h-9 w-full bg-transparent text-sm font-medium text-white outline-none placeholder:text-white/30 sm:h-14 sm:text-base"
                                        placeholder={t("enterPassword")}
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        className="text-white/55 transition-colors duration-300 hover:text-white"
                                        aria-label={showPassword ? t("hidePassword") : t("showPassword")}
                                    >
                                        {showPassword ? <IoEyeOff className="text-lg" /> : <IoEye className="text-lg" />}
                                    </button>
                                </div>
                            </label>

                            <div className="-mt-1 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleForgotPassword}
                                    disabled={resetSubmitting || submitting}
                                    className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#79EAFF] transition-colors duration-300 hover:text-white disabled:cursor-not-allowed disabled:opacity-60 sm:text-xs sm:tracking-[0.14em]"
                                >
                                    {resetSubmitting ? t("sending") : t("forgotPassword")}
                                </button>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full rounded-full bg-[linear-gradient(180deg,_#A0F8FF_0%,_#00EDFF_38%,_#00B8EE_100%)] px-5 py-2.5 text-xs font-black uppercase tracking-[0.14em] text-slate-950 shadow-[0_20px_40px_-28px_rgba(0,151,201,1)] transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:px-6 sm:py-3.5 sm:text-sm sm:tracking-[0.18em]"
                            >
                                {submitting ? t("loading") : t("login")}
                            </button>
                        </form>

                        <div className="mt-3 border-t border-white/8 pt-3 sm:mt-6 sm:pt-5">
                            <p className="text-xs text-white/62 sm:text-sm">
                                {t("dontHaveAccount")}{" "}
                                <Link to="/register" className="font-bold text-[#79EAFF] transition-colors duration-300 hover:text-white">
                                    {t("register")}
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Login;